import { useEffect, useState } from "react";
import { Tablet } from "lucide-react";
import {
  fetchGuestTabletWorkstations,
  fetchGuestTabletWorkstationsPublic,
} from "@/lib/guestTabletApi";
import {
  readCachedGuestTabletWorkstations,
  readGuestTabletWorkstationCode,
  writeCachedGuestTabletWorkstations,
  writeGuestTabletWorkstationCode,
} from "@/lib/guestTabletWorkstationStorage";
import type { GuestTabletWorkstation } from "@/types/guestTablet";

type Props = {
  value: string | null;
  onChange: (code: string | null) => void;
  disabled?: boolean;
  /** Vor Login: öffentliche Arbeitsplatz-Liste (ohne Auth). */
  publicMode?: boolean;
  className?: string;
  showNoneOption?: boolean;
};

export function GuestTabletWorkstationSelect({
  value,
  onChange,
  disabled,
  publicMode = false,
  className,
  showNoneOption = true,
}: Props) {
  const [workstations, setWorkstations] = useState<GuestTabletWorkstation[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const applyRows = (rows: { code: string; label: string; is_active?: boolean }[]) => {
      const active = rows
        .filter((w) => w.is_active !== false)
        .map((w) => ({
          id: w.code,
          code: w.code,
          label: w.label,
          is_active: true,
          sort_order: 0,
        }));
      setWorkstations(active);
      writeCachedGuestTabletWorkstations(active.map((w) => ({ code: w.code, label: w.label })));
    };

    const load = async () => {
      try {
        if (publicMode) {
          const rows = await fetchGuestTabletWorkstationsPublic();
          if (!cancelled) applyRows(rows);
          return;
        }
        const rows = await fetchGuestTabletWorkstations();
        if (!cancelled) applyRows(rows);
      } catch {
        if (!cancelled) {
          const cached = readCachedGuestTabletWorkstations();
          if (cached.length > 0) {
            applyRows(cached);
          } else {
            setWorkstations([]);
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [publicMode]);

  useEffect(() => {
    if (loading || value !== null) return;
    const stored = readGuestTabletWorkstationCode();
    if (!stored) return;
    const match = workstations.find((w) => w.code === stored);
    if (match) onChange(match.code);
  }, [loading, value, workstations, onChange]);

  function handleChange(next: string) {
    if (!next) {
      writeGuestTabletWorkstationCode(null);
      onChange(null);
      return;
    }
    writeGuestTabletWorkstationCode(next);
    onChange(next);
  }

  if (loading && workstations.length === 0) {
    return (
      <p className={className ?? "text-xs text-label-tertiary"}>Gäste-Tablets werden geladen…</p>
    );
  }

  if (workstations.length === 0 && !showNoneOption) {
    return null;
  }

  return (
    <label className={className ?? "flex min-w-0 items-center gap-2 text-sm"}>
      <Tablet className="h-4 w-4 shrink-0 text-label-tertiary" aria-hidden />
      <span className="shrink-0 text-label-secondary">Gäste-Tablet</span>
      <select
        className="min-w-0 max-w-[14rem] truncate rounded-lg border border-separator bg-surface px-2 py-1.5 text-sm text-label"
        value={value ?? ""}
        disabled={disabled}
        onChange={(e) => handleChange(e.target.value)}
        aria-label="Gäste-Tablet für Check-in auswählen"
      >
        {showNoneOption ? <option value="">Kein Tablet</option> : null}
        {workstations.map((ws) => (
          <option key={ws.code} value={ws.code}>
            {ws.label}
          </option>
        ))}
      </select>
    </label>
  );
}
